import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  Skeleton,
  alpha,
} from '@mui/material';
import { SEARCE_COLORS } from '../../theme';
import { ColumnConfig, CurrentTabRow, formatDateEnhanced } from '../../utils/customerSentimentutils';
import { TableHeader } from './TableHeader';
import { TypeChip } from './TypeChip';
import { useMuiTableColumnResize } from '../../hooks/useMuiTableColumnResize';
import { CUSTOMER_SENTIMENT_DEFAULT_COLUMN_PX } from './customerSentimentColumnWidths';

interface MyProjectsTableProps {
  columns: ColumnConfig[];
  data: CurrentTabRow[];
  loading: boolean;
  error: string | null;
  onSort: (columnId: string) => void;
  onFilterClick: (columnId: string, event: React.MouseEvent<HTMLElement>) => void;
  isFilterActive: (columnId: string) => boolean;
  renderFilterMenu: (columnId: string, columnLabel: string) => React.ReactNode;
  onCreateRequest: () => void;
}

export const MyProjectsTable: React.FC<MyProjectsTableProps> = ({
  columns,
  data,
  loading,
  error,
  onSort,
  onFilterClick,
  isFilterActive,
  renderFilterMenu,
  onCreateRequest,
}) => {
  const colResize = useMuiTableColumnResize(CUSTOMER_SENTIMENT_DEFAULT_COLUMN_PX);
  const getColumnMinWidth = (columnId: string): string => {
    switch (columnId) {
      case 'solution_story_status':
        return '180px';
      case 'project_name':
      case 'feedback_status':
        return '150px';
      case 'project_code':
      case 'status':
      case 'request_status':
        return '112px';
      case 'account':
      case 'project_account_bu':
      case 'closure_date':
        return '128px';
      default:
        return '104px';
    }
  };

  return (
    <TableContainer>
      <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
        <TableHead>
          <TableHeader
            columns={columns}
            onSort={onSort}
            onFilterClick={onFilterClick}
            isFilterActive={isFilterActive}
            renderFilterMenu={renderFilterMenu}
            getColumnMinWidth={getColumnMinWidth}
            getColumnWidthPx={colResize.getWidth}
            onBeginColumnResize={colResize.beginResize}
            resizingColumnId={colResize.resizingColumnId}
          />
        </TableHead>
        <TableBody>
          {loading ? (
            [...Array(5)].map((_, index) => (
              <TableRow key={index}>
                {columns.map((_, cellIndex) => (
                  <TableCell key={cellIndex} align="center" sx={{ py: 1 }}>
                    <Skeleton variant="text" width="80%" sx={{ mx: 'auto' }} />
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : error ? (
            <TableRow>
              <TableCell colSpan={columns.length} align="center" sx={{ py: 4 }}>
                <Typography color="error">{error}</Typography>
              </TableCell>
            </TableRow>
          ) : data.length === 0 ? (
            <TableRow>
              <TableCell colSpan={columns.length} align="center" sx={{ py: 4 }}>
                <Typography color="textSecondary">
                  No projects found.
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            data.map((project: CurrentTabRow) => (
              <TableRow
                key={String((project as Record<string, unknown>).project_code)}
                sx={{
                  '&:hover': { backgroundColor: alpha(SEARCE_COLORS.primary, 0.02) },
                  '&:nth-of-type(even)': { backgroundColor: alpha(SEARCE_COLORS.neutral_100, 0.5) },
                }}
              >
                {columns.map((column) => {
                  const value = column.field(project as any);
                  const isStatusCol = column.id === 'status';
                  const isFeedbackStatusCol = column.id === 'feedback_status';
                  const isSolutionStoryStatusCol = column.id === 'solution_story_status';
                  const isActionCol = column.id === 'request_status';
                  const displayValue = column.isDate ? formatDateEnhanced(value) : (value || '-');
                  return (
                    <TableCell
                      key={column.id}
                      align="center"
                      sx={{ py: 1, ...colResize.colWidthSx(column.id) }}
                    >
                      {isActionCol ? (
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={onCreateRequest}
                          disabled={
                            (project as Record<string, unknown>).can_create_request === false
                          }
                          sx={{
                            textTransform: 'none',
                            borderColor: SEARCE_COLORS.primary,
                            color: SEARCE_COLORS.primary,
                            '&:hover': { backgroundColor: alpha(SEARCE_COLORS.primary, 0.08) },
                          }}
                        >
                          {(project as Record<string, unknown>).can_create_request === false
                            ? 'Complete'
                            : 'Create Request'}
                        </Button>
                      ) : isStatusCol ? (
                        value ? <TypeChip type={String(value)} /> : <Typography variant="body2">-</Typography>
                      ) : isFeedbackStatusCol ? (
                        <TypeChip type={String(value || '-')} />
                      ) : isSolutionStoryStatusCol ? (
                        <TypeChip type={String(value || '-')} />
                      ) : (
                        <Typography
                          variant="body2"
                          title={String(displayValue)}
                          sx={{
                            fontWeight: column.id === 'project_code' ? 600 : 400,
                            color: column.id === 'project_code' ? SEARCE_COLORS.primary : SEARCE_COLORS.textPrimary,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {displayValue}
                        </Typography>
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
